<template>
  <div>
    <div ref="quillEditor" style="min-height: 250px;"></div>
    <input type="file" ref="imageInput" style="display: none" @change="uploadImage" accept="image/*" />
    <input type="file" ref="audioInput" style="display: none" @change="uploadAudio" accept="audio/*" />
  <!--  <button class="btn btn-info" type="button" @click="triggerAudioUpload">رفع صوت</button-->
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from 'vue';
import Quill from 'quill';
import { uploadFileToFirebase, uploadAudioToFirebase } from '../firebase/Utils.js';
import { generateRandomString } from '../utils/utils.js';

const props = defineProps(['content', 'modelValue']);
const emit = defineEmits(['update:modelValue']);

const quillEditor = ref(null);
const imageInput = ref(null);
const audioInput = ref(null);
let quill = null;
let _content = ref(props.content || '');

// Custom Audio Blot
const BlockEmbed = Quill.import('blots/block/embed');

var icons = Quill.import('ui/icons');
icons['audio'] = '<i style="width: 18px;height:18px; float: left;" class="fa fa-file-audio" aria-hidden="true"></i>';

class AudioBlot extends BlockEmbed {
  static create(url) {
    let node = super.create();

    let audio = document.createElement('audio');
    audio.setAttribute('controls', true);    
    node.setAttribute('class', 'ql-align-center');
    audio.setAttribute('src', url);
    audio.style.width = '60vw';
    audio.setAttribute('type', 'audio/mpeg');
    node.appendChild(audio);
    return node;
  }
  static value(node) {
    return node.getAttribute('src');
  }
  static formats(node) {
    return node.getAttribute('data-align') || 'right';
  }
  format(name, value) {
    if (name === 'align') {
      if (value) {
        this.domNode.setAttribute('data-align', value);
        this.domNode.style.display = 'block';
        this.domNode.style.margin = value === 'center' ? '0 auto' : value === 'right' ? '0 0 0 auto' : value === 'left' ? '0 auto 0 0' : '';
        this.domNode.style.float = value === 'right' ? 'right' : value === 'left' ? 'left' : '';
      } else {
        this.domNode.removeAttribute('data-align');
        this.domNode.style.margin = '';
        this.domNode.style.float = '';
      }
    } else {
      super.format(name, value);
    }
  }
}

class ImageBlot extends BlockEmbed {
  static create(url) {
    let node = super.create();
    
    let _p=document.createElement('p');
    let img = document.createElement('img');
    node.setAttribute('class', 'ql-align-center');
    _p.setAttribute('class', 'ql-align-center');
    img.setAttribute('src', url);
    img.style.width = '70vw';
    _p.appendChild(img);
    node.appendChild(_p);
    return node;
  }
  static value(node) {
    return node.getAttribute('src');
  }
  static formats(node) {
    return node.getAttribute('data-align') || 'right';
  }
   format(name, value) {
    if (name === 'align') {
      if (value) {
        this.domNode.setAttribute('data-align', value);
        this.domNode.style.display = 'block';
        this.domNode.style.margin = value === 'center' ? '0 auto' : value === 'right' ? '0 0 0 auto' : value === 'left' ? '0 auto 0 0' : '';
        this.domNode.style.float = value === 'right' ? 'right' : value === 'left' ? 'left' : '';
      } else {
        this.domNode.removeAttribute('data-align');
        this.domNode.style.margin = '';
        this.domNode.style.float = '';
      }
    } else {
      super.format(name, value);
    }
  }
}
AudioBlot.blotName = 'audio';
AudioBlot.tagName = 'div';
Quill.register(AudioBlot, true);
ImageBlot.blotName = 'image_quill';
ImageBlot.tagName = 'div';
Quill.register(ImageBlot, true);

const customToolbar = [
  [{ header: [false, 2, 1] }],
  [{ size: ['small', false, 'large', 'huge'] }],
  ['bold', 'italic', 'underline'],
  [{ direction: 'rtl' }, { direction: '' }],
  [
    { align: '' },
    { align: 'center' },
    { align: 'right' },
    { align: 'justify' }
  ],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  ['image', 'video', 'link', 'audio'],
  ['clean']
];

watch(
  () => props.content,
  (newContent) => {
    if (newContent !== _content.value) {
      _content.value = newContent || '';
      if (quill && quill.root.innerHTML !== newContent) {
        quill.clipboard.dangerouslyPasteHTML(newContent || '');
      }
    }
  }
);

function triggerAudioUpload() {
  audioInput.value.click();
}

function triggerImageUpload() {
  imageInput.value.click();
}

const uploadImage = async (event) => {
  const file = event.target.files[0];
  if (file) {
    const url = await uploadFileToFirebase(file, 'articles/images/', `${file.name}_${generateRandomString(8)}`);
    const range = quill.getSelection(true);
    quill.insertEmbed(range.index, 'image_quill', url);
    quill.setSelection(range.index + 1);
    quill.format('width', '70vw');
  }
  imageInput.value.value = '';
};

const uploadAudio = async (event) => {
  const file = event.target.files[0];
  if (file) {
    const url = await uploadAudioToFirebase(file, 'articles/audios/', `${file.name}_${generateRandomString(8)}`);
    const range = quill.getSelection(true);
    quill.insertEmbed(range.index, 'audio', url);
    quill.setSelection(range.index + 1);
  }
  audioInput.value.value = '';
};

onMounted(() => {
  nextTick(() => {
    quill = new Quill(quillEditor.value, {
      theme: 'snow',
      modules: {
        toolbar: {
          container: customToolbar,
          handlers: {
            image: function () {
              triggerImageUpload();
            },
            audio: function () {
              triggerAudioUpload();
            }
          }
        },
        clipboard: {
          matchVisual: false
        },
        history: {
          delay: 1000,
          maxStack: 100,
          userOnly: true
        }
      },
      placeholder: ''
    });
    // Set RTL and right alignment by default
    quill.format('direction', 'rtl');
    quill.format('align', 'right');
    // Set initial content
    quill.clipboard.dangerouslyPasteHTML(_content.value || '');
    quill.on('text-change',  (delta, oldDelta, source) => {
      const html = quill.root.innerHTML;
      _content.value = html;
      emit('update:modelValue', html);
      if (source !== 'user') return;

        const range = quill.getSelection();
        if (!range || range.length > 0) return;
        const [currentLine] = quill.getLine(range.index);
        const [prevLine] = quill.getLine(range.index - 1);

        if (currentLine && prevLine) {
          const format = quill.getFormat(range.index - 1);
          if (format.align) {
            console.log(format.align);
            quill.formatLine(range.index, 2, { align: format.align, direction: format.direction});
          }
        }

    });
  });
});
</script>

<style scoped>
.ql-editor {
  direction: rtl;
  text-align: right;
  min-height: 250px;
}
.ql-toolbar {
  direction: rtl;
  text-align: right;
}
.ql-editor li:not(.ql-direction-rtl):before {
  margin-left: 0 !important;
}

</style>
